import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useWindowScroll } from "@uidotdev/usehooks";
import { Skeleton } from "@/components/ui/skeleton";
import SecondaryHeroSectionSwiper from "../components/homepage/SecondaryHeroSectionSwiper";
import GenericSectionSkeleton from "../components/homepage/GenericSectionSkeleton";
import MainHeroSectionSwiper from "../components/homepage/MainHeroSectionSwiper";
import CategoriesSwiper from "../components/homepage/CategoriesSwiper";
import { useHeroSectionExistance } from "../stores/useGroupStore";
import { api } from "../services/homepage";
import { HOMEPAGE } from "../constants/ui";
import { HasMorePages, HomepageSectionType, } from "../types/global";
import GenericHomepageSectionSwiper from "../components/homepage/GenericHomepageSectionSwiper";
import NavbarContext from "../components/navbar/NavbarContext";
function Section({ data }) {
    return (_jsxs(_Fragment, { children: [(data.type == HomepageSectionType.Custom ||
                data.type == HomepageSectionType.Lives ||
                data.type == HomepageSectionType.ComingSoon ||
                data.type == HomepageSectionType.News ||
                data.type == HomepageSectionType.Reels ||
                data.type == HomepageSectionType.Shorts) && (_jsx(_Fragment, { children: _jsx(GenericHomepageSectionSwiper, { data: data, className: "mb-5 ml-3 md:ml-10 lg:ml-18", showAllButton: true }) })), data.type == HomepageSectionType.Categories && (_jsx(CategoriesSwiper, { data: data, navigation: {
                    prevPosition: "left-0 -translate-x-1/2",
                    nextPosition: "right-6",
                }, className: `my-5 ml-3 md:ml-10 lg:mt-10 lg:ml-0 lg:w-screen! lg:pl-18` })), data.type == HomepageSectionType.SecondaryHeroSection && (_jsx(_Fragment, { children: _jsx(SecondaryHeroSectionSwiper, { data: data, className: "mx-3 md:mx-10 lg:mx-18" }) }))] }));
}
function Page({ data, index, pageList, skip, setSkip }) {
    const sections = data.sections;
    const isMainHeroSection = index == 0 &&
        sections &&
        sections.length > 0 &&
        sections[0].type == HomepageSectionType.MainHeroSection &&
        sections[0].content_list &&
        sections[0].content_list?.length > 0;
    React.useEffect(() => {
        if (isMainHeroSection) {
            for (let page = 0; page < pageList.length; page++) {
                const section = pageList[page].sections?.findIndex((section) => section.type !== HomepageSectionType.MainHeroSection &&
                    section.content_list?.length);
                if (section !== undefined && section !== -1) {
                    setSkip({ page, section });
                }
            }
            useHeroSectionExistance.getState().setExist(true);
        }
    }, [isMainHeroSection]);
    const sectionsRender = sections &&
        sections.map((sectionItem, sectionIndex) => sectionItem.content_list &&
            sectionItem.content_list?.length > 0 &&
            Boolean(index == skip?.page ? sectionIndex != skip?.section : true) && (_jsx(React.Fragment, { children: _jsx(Section, { data: sectionItem }) }, sectionIndex)));
    return (_jsx(_Fragment, { children: isMainHeroSection ? (_jsxs(_Fragment, { children: [_jsx(MainHeroSectionSwiper, { data: sections[0], children: skip && pageList[skip.page].sections?.[skip.section] && (_jsx(Section, { data: pageList[skip.page].sections[skip.section] })) }), sectionsRender] })) : (_jsx(_Fragment, { children: index == 0 ? (_jsx(NavbarContext, { className: "flex flex-col", children: sectionsRender })) : (_jsx(_Fragment, { children: sectionsRender })) })) }));
}
export default function Home() {
    const { isPending, data, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ["sections"],
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            return api.homepage.section({
                current_page: pageParam,
            });
        },
        getNextPageParam: (lastPage, __, lastPageParam) => lastPage.has_more == HasMorePages.True ? lastPageParam + 1 : null,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        refetchOnMount: true,
    });
    const currentPage = data?.pageParams[data.pageParams.length - 1] ?? 0;
    const pages = data?.pages;
    const [pageSectionToSkip, setPageSectionToSkip] = React.useState(null);
    const [{ x, y }] = useWindowScroll();
    React.useEffect(() => {
        let isMiddle = false;
        if (y) {
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            const percentage = maxScroll > 0 ? (y / maxScroll) * 100 : 100;
            isMiddle = percentage >= HOMEPAGE.SECTIONS_LOAD_SCROLL_TRESHOLD;
        }
        if (hasNextPage &&
            !isFetchingNextPage &&
            (currentPage < HOMEPAGE.SECTIONS_TO_LOAD_COUNT - 1 || isMiddle)) {
            fetchNextPage();
        }
    }, [data?.pages.length, hasNextPage, isFetchingNextPage, y, fetchNextPage]);
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    return (_jsx(_Fragment, { children: isPending || !pages || pages.length == 0 ? (_jsx(Skeleton, { className: "skeleton-custom h-[120vh] w-full rounded-none" })) : (_jsxs("div", { role: "main", className: "flex flex-col gap-2 overflow-x-hidden pb-20", children: [pages.map((item, index) => (_jsx(Page, { data: item, index: index, pageList: pages, skip: pageSectionToSkip, setSkip: setPageSectionToSkip }, index))), isFetchingNextPage &&
                    Array.from({ length: 4 }).map((_, index) => (_jsx(GenericSectionSkeleton, { className: "ml-3 md:ml-10 lg:ml-18", type: "generic-landscape" }, index)))] })) }));
}
